import {
  ColumnDef,
  ColumnFiltersState,
  RowSelectionState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui";
import { cn } from "~/lib/utils";

import TableRowSkeleton from "../datatable/table-skeleton";
import { DataTableToolbar } from "../datatable/toolbar";
import { DataTablePagination } from "./pagination";

export type CustomColumnMeta = {
  omitRowClick?: boolean;
};

// Extend the ColumnDef type to include the custom `meta` property
export type ExtendedColumnDef<TData, TValue> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta;
};
interface DataTableProps<TData, TValue> {
  columns: ExtendedColumnDef<TData, TValue>[];
  data: TData[];

  tablePrefixComponent?: React.FC;

  onRowClick?: (row: TData) => void;

  toolbar?: { title: string; actions: React.ReactNode; backBtn?: boolean };
  isLoading?: boolean;
  rowSelection?: RowSelectionState;
  setRowSelection?: React.Dispatch<React.SetStateAction<RowSelectionState>>;
}

export function ClientDataTable<TData, TValue>({
  columns,
  data,
  isLoading,
  onRowClick,
  toolbar,
  rowSelection,
  setRowSelection,
  ...props
}: DataTableProps<TData, TValue>) {
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );

  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  return (
    <div className="w-full space-y-4">
      <DataTableToolbar toolbar={toolbar} />
      {props.tablePrefixComponent && <props.tablePrefixComponent />}

      <Table className="rounded-t-2xl bg-white">
        <TableHeader className="w-full text-black">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    className="sticky top-0 z-10 font-Bold text-black"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        {isLoading ? (
          <TableRowSkeleton
            columns={columns.length}
            rows={table.getState().pagination.pageSize}
          />
        ) : (
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={
                    rowSelection
                      ? row?.getIsSelected() && "selected"
                      : undefined
                  }
                  className={cn(
                    "text-primary-500",
                    // getRowHoverClass?.(row.original),
                    {
                      "bg-secondary-200": rowSelection && row?.getIsSelected(),
                    },
                  )}
                  // data-state={row.getIsSelected() && "selected"}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {/* {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))} */}
                  {row.getVisibleCells().map((cell) => {
                    const isNonClickable = (
                      cell.column.columnDef as ExtendedColumnDef<TData, TValue>
                    ).meta?.omitRowClick;

                    return (
                      <TableCell
                        key={cell.id}
                        className={cn("", {
                          "hover:cursor-pointer": onRowClick && !isNonClickable,
                        })}
                        onClick={
                          isNonClickable
                            ? (e) => e.stopPropagation()
                            : undefined
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        )}
      </Table>

      <DataTablePagination table={table} />
    </div>
  );
}
